import React from "react";

import forBasicControl from "./forBasicControl";
import { IControlProps, IBasicControlDefinition } from "./types";
import { FormGroup, FormFeedback } from "reactstrap";
import { createLabel } from "./BasicControls";
import FileUploader, { IFile } from "../FileUploader";
import { AttachmentType, IAttachmentReference } from "../../api/types";
import { deleteApplicationAttachment, submitApplicationAttachment } from "../../api/application";
import LabelWithToolTip from "../LabelWithToolTip";

export interface IFileControl extends IBasicControlDefinition {
    maxFiles: number;
    attachmentType: AttachmentType;
}

interface IProps extends IControlProps<IFile[], IFileControl> {
}
// Note this control expects the contact to contain to methods uploadAttachment(file: IFile): Promise<IFile> and deleteAttachment(file: IFile)
// this is not ideal but it is the most efficient way to upload the files directly (and not in the save of the form as the design more or less expects)
// DidMount does valdiate the existance of the context and methods so if missing it will throw an exception
// The helper method createFileControlContext allows easy creation of this (part of) context

export interface IFileControlContext {
    deleteAttachment: (file: IFile, type: AttachmentType) => Promise<void>;
    uploadAttachment: (file: IFile, type: AttachmentType) => Promise<IFile>;
}
export function createFileControlContext(applicationID: string, onBusy: (state: false | 'uploading' | 'deleting') => void): IFileControlContext {
    async function deleteAttachment(file: IFile, type: AttachmentType): Promise<void> {
        onBusy('deleting');
        try {
            await deleteApplicationAttachment(applicationID, type, file.name)
        }
        finally {
            onBusy(false);
        }
    }
    async function uploadAttachment(file: IFile, type: AttachmentType): Promise<IAttachmentReference> {
        onBusy('uploading');
        try {
            return await submitApplicationAttachment(applicationID, type, file.name, file.file);
        }
        finally {
            onBusy(false);
        }
    }

    return { deleteAttachment, uploadAttachment };
}

class FileControl extends React.PureComponent<IProps> {
    public componentDidMount() {
        if (!this.controlContext || typeof (this.controlContext.uploadAttachment) !== 'function' || typeof (this.controlContext.deleteAttachment) !== 'function')
            throw new Error(`FileControl (${this.props.definition.key}) context invalid or missing`);
    }
    private get controlContext(): IFileControlContext {
        return this.props.context;
    }
    private async doDelete(file: IFile) {
        await this.controlContext.deleteAttachment(file, this.props.definition.config.attachmentType);
        this.props.onChange(this.props.value.filter((x) => file.name !== x.name));
    }
    private async doUpload(file: IFile) {
        const result = await this.controlContext.uploadAttachment(file, this.props.definition.config.attachmentType);
        if (result.file)
            delete result.file;
        this.props.onChange(this.props.value.filter((x) => file.name !== x.name && result.name !== x.name).concat([result]));
        return result;
    }
    public render() {
        const props = this.props;

        // using formgroup here breaks the behaviour of rows placing the elements horizontally
        return <>
            <FormGroup style={{ display: 'initial' }} className={"file-drop " + (props.showValidation ? "was-validated" : "")}>
                <LabelWithToolTip
                    label={createLabel(props.definition, true)}
                    field={props.definition.key}
                    tooltip={props.definition.config.description}/>

                <div className={props.showValidation ? (props.isValid ? "is-valid" : "is-invalid") : ""}>
                    <FileUploader
                        maxFiles={props.definition.config.maxFiles}
                        files={props.value || []}
                        onFileUpload={(file) => this.doUpload(file)}
                        onFileDelete={(file) => this.doDelete(file)} />
                </div>
                {!props.isValid && <FormFeedback valid={false}>{props.validationMessage}</FormFeedback>}

            </FormGroup>
        </>
    }
}

export default forBasicControl(FileControl);
